import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import { FaCheckCircle, FaExternalLinkAlt, FaAngleLeft } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { PiShieldWarningBold } from "react-icons/pi";
import config from "../../../config/config";
import axios from "axios";
import { RotatingLines, ThreeDots } from "react-loader-spinner";

const BankDetailsComponent = ({ user }) => {
  const location = useLocation();
  const [bankDetails, setBankDetails] = useState({
    email: "",
    country: "",
  });
  const [isChecked, setIsChecked] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [accountId, setAccountId] = useState("");
  const [accountSetupLink, setAccountSetupLink] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [paymentDetailsLinked, setPaymentDetailsLinked] = useState(false);
  const { isSmallScreen, handleBackClick } = useOutletContext();

  const baseURL = config.API_BASE_URL;

  const countries = [
    { text: "United States", value: "US", isSelected: false },
    { text: "Canada", value: "CA", isSelected: false },
    { text: "Australia", value: "AU", isSelected: false },
    { text: "United Kingdom", value: "UK", isSelected: false },
  ];

  const [countryButtons, setCountryButtons] = useState(countries);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (user && user.id) {
        try {
          const response = await axios.get(
            `${baseURL}/v1/worker-users/${user.id}`
          );
          const fetchedUserData = response.data;

          if (
            fetchedUserData.paymentDetail &&
            fetchedUserData.paymentDetail.accountId
          ) {
            console.log(
              "Fetched Country:",
              fetchedUserData.paymentDetail.accountCountry
            );
            console.log("Agreement Signed: Yes");

            const updatedButtons = countries.map((country) => ({
              ...country,
              isSelected:
                country.value === fetchedUserData.paymentDetail.accountCountry,
            }));

            setCountryButtons(updatedButtons);

            setBankDetails({
              email: fetchedUserData.email,
              country: fetchedUserData.paymentDetail.accountCountry,
            });
            setIsChecked(true);
            setIsReadOnly(true);
            setPaymentDetailsLinked(true);
          } else {
            console.log("Fetched Country: None");
            console.log("Agreement Signed: No");

            setBankDetails({
              email: fetchedUserData.email,
              country: "",
            });
            setIsChecked(false);
            setIsReadOnly(false);
            setPaymentDetailsLinked(false);
          }

          validateForm({
            email: fetchedUserData.email,
            country: fetchedUserData.paymentDetail?.accountCountry,
            isChecked: fetchedUserData.paymentDetail ? true : false,
          });
        } catch (error) {
          console.error("Failed to fetch user email:", error);
        }
      }
    };

    fetchUserDetails();
  }, [user, baseURL]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const success = queryParams.get("success");

    console.log("Query params:", queryParams.toString());
    console.log("Success param:", success);

    if (success === "true") {
      const token = queryParams.get("token");

      console.log("Token from URL:", token);

      if (token) {
        try {
          const decodedToken = atob(token);
          console.log("Decoded token:", decodedToken);
          const [, accountId] = decodedToken.split(":");
          console.log("Account ID from token:", accountId);

          const fetchAccountDetails = async () => {
            try {
              const response = await axios.get(
                `${baseURL}/v1/stripe/accounts/${accountId}`
              );
              const accountDetails = response.data;
              console.log("Account Details:", accountDetails);

              if (
                Object.values(accountDetails.capabilities).includes("pending")
              ) {
                setTimeout(fetchAccountDetails, 3000);
              } else {
                console.log("Account capabilities are active.");
                handleUpdatePaymentDetails(accountDetails);
              }
            } catch (error) {
              console.error("Failed to fetch account details:", error);
            }
          };

          fetchAccountDetails();
        } catch (error) {
          console.error("Failed to decode token:", error);
        }
      }
    }
  }, [baseURL]);

  const handleUpdatePaymentDetails = async (accountDetails) => {
    console.log("handleUpdatePaymentDetails is called");

    const paymentDetails = {
      accountId: accountDetails.accountId,
      accountType: accountDetails.accountType,
      accountCountry: accountDetails.accountCountry,
      accountCreatedAt: accountDetails.accountCreatedAt,
      capabilities: accountDetails.capabilities,
      paymentDetailsAllowEdit: accountDetails.paymentDetailsAllowEdit,
    };

    console.log("Payment Details to be updated:", paymentDetails);

    try {
      const updateUserResponse = await axios.patch(
        `${baseURL}/v1/worker-users/${user.id}`,
        {
          paymentDetails: paymentDetails,
        }
      );
      if (updateUserResponse.data) {
        console.log("Patch successful");
        setShowModal(true);
      } else {
        console.log("Patch succeeded but no data returned");
      }
    } catch (error) {
      console.error("Failed to patch user data:", error);
    }
  };

  const toggleCountrySelection = (index) => {
    if (isReadOnly) return;
    const selectedCountry = countryButtons[index];
    const updatedButtons = countryButtons.map((country, i) =>
      i === index
        ? { ...country, isSelected: !country.isSelected }
        : { ...country, isSelected: false }
    );

    setCountryButtons(updatedButtons);
    const newCountryValue = selectedCountry.isSelected
      ? ""
      : selectedCountry.value;

    const newBankDetails = {
      ...bankDetails,
      country: newCountryValue,
    };

    setBankDetails(newBankDetails);

    validateForm({ ...newBankDetails, isChecked });
  };

  const handleCheckboxChange = () => {
    if (isReadOnly) return;
    const newCheckedState = !isChecked;
    setIsChecked(newCheckedState);
    validateForm({ ...bankDetails, isChecked: newCheckedState });
  };

  const validateForm = (details = bankDetails) => {
    const newErrors = {};

    if (!details.country) {
      newErrors.country = "Country is required.";
    }

    if (!details.isChecked) {
      newErrors.isChecked = "You must agree to the terms.";
    }

    setErrors(newErrors);

    const isValid = Object.keys(newErrors).length === 0;
    setIsButtonDisabled(!isValid);

    return isValid;
  };

  const handleAccountSetup = async () => {
    setSubmitAttempted(true);
    if (validateForm({ ...bankDetails, isChecked })) {
      setIsLoading(true);
      const bankDetailsData = {
        email: bankDetails.email,
        country: bankDetails.country,
      };

      try {
        const response = await axios.post(
          `${baseURL}/v1/stripe/create-standard-account`,
          bankDetailsData
        );
        if (response.data) {
          setShowModal(true);
          setSuccessMessage("Account Setup Link created successfully!");
          const accountId = response.data.accountId;
          const accountSetupLink = response.data.accountSetupLink;
          setAccountId(accountId);
          setAccountSetupLink(accountSetupLink);
          setIsButtonDisabled(false);

          console.log("Account ID:", accountId);
          console.log("Account Setup Link:", accountSetupLink);
        }
      } catch (error) {
        console.error("Failed to create account", error);
      } finally {
        setIsLoading(false);
      }
    } else {
      console.error("Validation failed.", errors);
    }
  };

  const openAccountSetupLink = () => {
    if (accountId && accountSetupLink) {
      window.open(accountSetupLink, "_blank");
    } else {
      console.error("Account setup link is not available.");
    }
  };

  return (
    <div className="bank-account-component-container">
      <div className="bank-indicative-above-div1">
      <div className="profile-back-container">
        {isSmallScreen && (
            <div className="profile-back-arrow" onClick={handleBackClick}>
              <FaAngleLeft className="profile-back-icon"/> 
            </div>
          )}
          <h2>Payment Details</h2>
        </div>
        
        <p>
          To get you paid as soon as possible, select your country below. You'll
          then receive a link to set up your account and link it to{" "}
          <strong>Stripe</strong>, our secure payment processor.
        </p>
        <p>
          <strong>One Support Hub</strong> partners with <strong>Stripe</strong>{" "}
          for secure financial services and process payments to you on behalf of
          your clients.
        </p>
      </div>
      {paymentDetailsLinked && (
        <div className="success-message-container">
          <div className="claim-success-message">
            <FaCheckCircle className="invoice-check-icon" />
            <h4>Payment setup complete. You're all set to receive payments.</h4>
          </div>
        </div>
      )}

      <div className="bank-info-div">
        <p>
          Banking information is concealed to protect privacy and is utilized
          for secure transactions. This information will not be visible on your
          profile.
        </p>
      </div>

      <div className="bank-details-section">
        <div className="country-buttons-container">
          <h3>
            Your country <span className="required-indicator">*</span>
          </h3>
          {countryButtons.map((country, index) => (
            <button
              key={country.text}
              className={`language-horizontal-button ${
                country.isSelected ? "selected" : ""
              }`}
              onClick={() => toggleCountrySelection(index)}
              disabled={isReadOnly}
            >
              <div className="button-checkmark">
                {country.isSelected ? "✓" : ""}
              </div>
              <div className="button-text">{country.text}</div>
            </button>
          ))}
          {errors.country && <div className="bank-error">{errors.country}</div>}
        </div>
        <div className="bank-detail">
          <h3>
            Email <span className="required-indicator">*</span>
          </h3>
          <input type="text" name="email" value={bankDetails.email} readOnly />
          {errors.email && <div className="bank-error">{errors.email}</div>}
        </div>
      </div>

      <div className="bank-checkbox-section">
        <div className="checkbox-container">
          <div className="checkbox-column">
            <input
              type="checkbox"
              id="bankCheckbox"
              checked={isChecked}
              onChange={handleCheckboxChange}
              disabled={isReadOnly}
            />
            <label htmlFor="bankCheckbox">
              I understand I'm responsible for accurate Bank Account details to
              receive timely payments.
            </label>
          </div>
        </div>
        {errors.isChecked && (
          <div className="bank-error">You must agree to the terms.</div>
        )}
      </div>

      {showModal && !paymentDetailsLinked && (
        <div className="success-message-container">
          <div className="claim-success-message">
            <FaCheckCircle className="invoice-check-icon" />
            <h4>{successMessage}</h4>
          </div>
        </div>
      )}
      {paymentDetailsLinked && (
        <div className="warning-message-container">
          <div className="warning-message">
            <PiShieldWarningBold className="warning-icon" />
            <p>
              To modify or change your linked payment details, Please contact
              Customer Support.
            </p>
          </div>
        </div>
      )}
      <div className="bank-save">
        {isLoading ? (
          <div className="account-setup-loading-spinner">
            <ThreeDots
              visible={true}
              height="60"
              width="50"
              color="#4fa94d"
              radius="9"
              ariaLabel="three-dots-loading"
              wrapperStyle={{}}
              wrapperClass=""
            />
          </div>
        ) : (
          <>
            {!accountSetupLink && !paymentDetailsLinked && (
              <button onClick={handleAccountSetup} disabled={isButtonDisabled}>
                Generate Setup Link
              </button>
            )}
            {accountSetupLink && (
              <button
                onClick={openAccountSetupLink}
                disabled={isButtonDisabled}
              >
                Setup Your Account <FaExternalLinkAlt />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BankDetailsComponent;
